import React, { useMemo } from 'react';
import { WholeCandleAnalysis, TrailingOHLC } from '@shared/types';
import {
  computeTrailingCandleMetrics,
  ParentCandleOHLC,
} from '../../utils/trailingCandleMetrics';
import './WholeCandleContinuationInfoOverlay.css';

interface WholeCandleContinuationInfoOverlayProps {
  analysis: WholeCandleAnalysis | null;
  isLoading?: boolean;
  /**
   * Trailing OHLC for the SAME candle the analysis describes — the mini-bar the
   * chart draws offset in its own colour, covering only the final N seconds.
   * Omit (or pass null) when unavailable and the trailing row is hidden.
   */
  trailing?: TrailingOHLC | null;
}

const WholeCandleContinuationInfoOverlay: React.FC<WholeCandleContinuationInfoOverlayProps> = React.memo(({ analysis, isLoading, trailing }) => {
  // Trailing bar metrics. Percentages are against the parent's high-low range,
  // the same denominator WCC uses for `candleLength` and its wick percentage.
  const trailingData = useMemo(() => {
    if (!analysis) return null;
    const parent: ParentCandleOHLC = {
      open: analysis.open,
      high: analysis.high,
      low: analysis.low,
      close: analysis.close,
    };
    return computeTrailingCandleMetrics(parent, trailing);
  }, [analysis, trailing]);

  const displayData = useMemo(() => {
    if (!analysis) return null;

    // Wick - always shown
    const wickPctStr = analysis.combinedWickPct.toFixed(0);
    const wickRangeStr = `${analysis.wickThresholdMin}-${analysis.wickThresholdMax}`;

    // Length - always show, but threshold only if filter enabled
    const lengthStr = analysis.candleLength.toFixed(0);

    // ATR - always show, but threshold only if filter enabled
    const atrStr = analysis.atrValue.toFixed(1);

    // HOD/LOD distances
    const hodDistStr = analysis.distanceFromHOD.toFixed(0);
    const lodDistStr = analysis.distanceFromLOD.toFixed(0);

    // Verdict
    let verdictIcon = '';
    let verdictText = '';
    let verdictClass = '';

    if (analysis.isSignal) {
      verdictIcon = '✓';
      verdictText = analysis.signalType === 'bullish' ? 'LONG' : 'SHORT';
      verdictClass = 'signal';
    } else if (analysis.wasSkipped) {
      verdictIcon = '⚠';
      verdictText = analysis.rejectionReasons[0] || `SKIP: ${analysis.skipReason}`;
      verdictClass = 'skip';
    } else {
      verdictIcon = '✗';
      verdictText = analysis.rejectionReasons[0] || 'NO SIGNAL';
      verdictClass = 'no-signal';
    }

    return {
      wickPctStr,
      wickRangeStr,
      isWickInRange: analysis.isWickInRange,
      lengthStr,
      minLengthStr: analysis.minCandleLengthPoints.toFixed(0),
      skipIfSmallCandle: analysis.skipIfSmallCandle,
      passesLengthFilter: analysis.passesLengthFilter,
      atrStr,
      atrMinStr: analysis.atrMinThreshold.toFixed(0),
      skipIfLowATR: analysis.skipIfLowATR,
      passesAtrFilter: analysis.passesAtrFilter,
      hodDistStr,
      skipIfHOD: analysis.skipIfHOD,
      passesHodFilter: analysis.passesHodFilter,
      lodDistStr,
      skipIfLOD: analysis.skipIfLOD,
      passesLodFilter: analysis.passesLodFilter,
      hodLodRangePoints: analysis.hodLodRangePoints,
      verdictIcon,
      verdictText,
      verdictClass,
    };
  }, [analysis]);

  if (isLoading) {
    return (
      <div className="wcc-info-overlay">
        <div className="wcc-info-content">
          <span className="wcc-info-loading">...</span>
        </div>
      </div>
    );
  }

  if (!analysis || !displayData) {
    return (
      <div className="wcc-info-overlay">
        <div className="wcc-info-content">
          <span className="wcc-info-icon">📊</span>
          <span className="wcc-info-value neutral">WCC: –</span>
        </div>
      </div>
    );
  }

  return (
    <div className="wcc-info-overlay">
      <div className="wcc-info-content">
        {/* Metrics row */}
        <div className="wcc-info-metrics">
          {/* Wick - always shown */}
          <div className="wcc-info-item">
            <span className="wcc-info-label">Wick</span>
            <span className={`wcc-info-value ${displayData.isWickInRange ? 'pass' : 'fail'}`}>
              {displayData.wickPctStr}%
            </span>
            <span className="wcc-info-threshold">({displayData.wickRangeStr})</span>
            <span className={`wcc-info-check ${displayData.isWickInRange ? 'pass' : 'fail'}`}>
              {displayData.isWickInRange ? '✓' : '✗'}
            </span>
          </div>

          <span className="wcc-info-separator">|</span>

          {/* Length - always shown, threshold if filter enabled */}
          <div className="wcc-info-item">
            <span className="wcc-info-label">Len</span>
            {displayData.skipIfSmallCandle ? (
              <>
                <span className={`wcc-info-value ${displayData.passesLengthFilter ? 'pass' : 'fail'}`}>
                  {displayData.lengthStr}/{displayData.minLengthStr}pt
                </span>
                <span className={`wcc-info-check ${displayData.passesLengthFilter ? 'pass' : 'fail'}`}>
                  {displayData.passesLengthFilter ? '✓' : '✗'}
                </span>
              </>
            ) : (
              <span className="wcc-info-value neutral">{displayData.lengthStr}pt</span>
            )}
          </div>

          <span className="wcc-info-separator">|</span>

          {/* ATR - always shown, threshold if filter enabled */}
          <div className="wcc-info-item">
            <span className="wcc-info-label">ATR</span>
            {displayData.skipIfLowATR ? (
              <>
                <span className={`wcc-info-value ${displayData.passesAtrFilter ? 'pass' : 'fail'}`}>
                  {displayData.atrStr}/{displayData.atrMinStr}
                </span>
                <span className={`wcc-info-check ${displayData.passesAtrFilter ? 'pass' : 'fail'}`}>
                  {displayData.passesAtrFilter ? '✓' : '✗'}
                </span>
              </>
            ) : (
              <span className="wcc-info-value neutral">{displayData.atrStr}</span>
            )}
          </div>

          <span className="wcc-info-separator">|</span>

          {/* HOD - always shown, threshold if filter enabled */}
          <div className="wcc-info-item">
            <span className="wcc-info-label">HOD</span>
            {displayData.skipIfHOD ? (
              <>
                <span className={`wcc-info-value ${displayData.passesHodFilter ? 'pass' : 'fail'}`}>
                  {displayData.hodDistStr}pt
                </span>
                <span className={`wcc-info-check ${displayData.passesHodFilter ? 'pass' : 'fail'}`}>
                  {displayData.passesHodFilter ? '✓' : '✗'}
                </span>
              </>
            ) : (
              <span className="wcc-info-value neutral">{displayData.hodDistStr}pt</span>
            )}
          </div>

          <span className="wcc-info-separator">|</span>

          {/* LOD - always shown, threshold if filter enabled */}
          <div className="wcc-info-item">
            <span className="wcc-info-label">LOD</span>
            {displayData.skipIfLOD ? (
              <>
                <span className={`wcc-info-value ${displayData.passesLodFilter ? 'pass' : 'fail'}`}>
                  {displayData.lodDistStr}pt
                </span>
                <span className={`wcc-info-check ${displayData.passesLodFilter ? 'pass' : 'fail'}`}>
                  {displayData.passesLodFilter ? '✓' : '✗'}
                </span>
              </>
            ) : (
              <span className="wcc-info-value neutral">{displayData.lodDistStr}pt</span>
            )}
          </div>
        </div>

        {/* Trailing mini-candle (final N seconds) — lengths in points, and how
            much of the PARENT candle's range each one accounts for. Hidden
            entirely when the chart has no valid trailing bar for this candle. */}
        {trailingData && (
          <div className="wcc-info-metrics wcc-info-trailing">
            <div className="wcc-info-item">
              <span className="wcc-info-label wcc-info-trailing-tag">
                {trailingData.durationSeconds}s
              </span>
            </div>

            <div className="wcc-info-item">
              <span className="wcc-info-label">Len</span>
              <span className={`wcc-info-value ${trailingData.isBullish ? 'pass' : 'fail'}`}>
                {trailingData.totalPts.toFixed(1)}pt
              </span>
              <span className="wcc-info-threshold">
                ({trailingData.totalPctOfParent.toFixed(0)}%)
              </span>
            </div>

            <span className="wcc-info-separator">|</span>

            <div className="wcc-info-item">
              <span className="wcc-info-label">Body</span>
              <span className={`wcc-info-value ${trailingData.isBullish ? 'pass' : 'fail'}`}>
                {trailingData.bodyPts.toFixed(1)}pt
              </span>
              <span className="wcc-info-threshold">
                ({trailingData.bodyPctOfParent.toFixed(0)}%)
              </span>
            </div>

            <span className="wcc-info-separator">|</span>

            <div className="wcc-info-item">
              <span className="wcc-info-label">UW</span>
              <span className="wcc-info-value neutral">
                {trailingData.upperWickPts.toFixed(1)}pt
              </span>
              <span className="wcc-info-threshold">
                ({trailingData.upperWickPctOfParent.toFixed(0)}%)
              </span>
            </div>

            <span className="wcc-info-separator">|</span>

            <div className="wcc-info-item">
              <span className="wcc-info-label">LW</span>
              <span className="wcc-info-value neutral">
                {trailingData.lowerWickPts.toFixed(1)}pt
              </span>
              <span className="wcc-info-threshold">
                ({trailingData.lowerWickPctOfParent.toFixed(0)}%)
              </span>
            </div>
          </div>
        )}

        {/* Verdict - on same line desktop, separate on mobile */}
        <div className="wcc-info-verdict-container">
          <span className={`wcc-info-verdict ${displayData.verdictClass}`}>
            {displayData.verdictIcon} {displayData.verdictText}
          </span>
        </div>
      </div>
    </div>
  );
});

export default WholeCandleContinuationInfoOverlay;
